import { getYValue } from './builder.js';
import { isThresholdDynamic } from './type-guards.js';
import { type ThresholdIndicatorData } from '../../../core/components/threshold-bar/indicators/ThresholdIndicator.js';
import { type ThresholdMarker } from '../../../core/components/threshold-bar/ThresholdBar.js';
import { getMarkerValue } from '../../../core/components/threshold-bar/utils/get-marker-value.js';
import { getThresholdPixel } from '../../../core/components/threshold-bar/utils/get-threshold-pixel.ts.js';
import type { AnyNumberScale } from '../../../core/types/scales.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import type {
  AxisIdToFormatter,
  XYChartAxisInternal,
  XYChartThresholdsPropsInternal,
} from '../../types/xy-chart-internals.js';
import type {
  XYChartThresholdDataLineStatic,
  XYChartThresholdDataRangeStatic,
  XYChartThresholdDynamicData,
  XYChartYAxisPosition,
} from '../../types/xy-chart.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { getAxisFormatterWithTimeFallback } from '../overlay/utils/get-axis-formatter-with-time-fallback.js';

interface ThresholdMarkersReducerConfig {
  axis: 'left' | 'right';
  axisConfig?: XYChartAxisInternal;
  axesIdToScales: AxisIdToScales;
  axesIdToFormatter: AxisIdToFormatter;
  width: number;
}

/**
 * Returns a reducer function to accumulate threshold markers with scale and formatted value.
 */
export function markersReducer({
  axis,
  axisConfig,
  axesIdToScales,
  axesIdToFormatter,
  width,
}: ThresholdMarkersReducerConfig) {
  return (
    acc: ThresholdMarker[],
    threshold: XYChartThresholdsPropsInternal[number],
  ) => {
    if (axisPositionIncludes(axis)(threshold.position)) {
      const scale = getCurrentScale(
        axesIdToScales,
        threshold.yAxisId,
      ) as AnyNumberScale;

      if (isThresholdDynamic(threshold)) {
        let markerDomain;
        if (axis === 'right') {
          markerDomain = {
            y0: getYValue(threshold.data[threshold.data.length - 1], 'min'),
            y1: getYValue(threshold.data[threshold.data.length - 1], 'max'),
          };
        } else {
          markerDomain = {
            y0: getYValue(threshold.data[0], 'min'),
            y1: getYValue(threshold.data[0], 'max'),
          };
        }

        acc.push({
          ...threshold,
          y0: getThresholdPixel(markerDomain.y0, scale),
          y1: getThresholdPixel(markerDomain.y1, scale),
          value: 'dynamic',
        });
      } else {
        const markerDomain = {
          y0: getYValue(threshold.data, 'min'),
          y1: getYValue(threshold.data, 'max'),
        };

        acc.push({
          ...threshold,
          y0: getThresholdPixel(markerDomain.y0, scale),
          y1: getThresholdPixel(markerDomain.y1, scale),
          value: getMarkerValue(
            markerDomain,
            getAxisFormatterWithTimeFallback({
              axisConfig,
              axisId: threshold.yAxisId,
              scale,
              axesIdToFormatter,
              width,
            }),
          ),
        });
      }
    }
    return acc;
  };
}

export const axesPositionsIncludes = (
  axisPosition: 'left' | 'right',
  axesPositions: { position: XYChartYAxisPosition }[],
) => {
  const expectedPositionsIncludes = axisPositionIncludes(axisPosition);

  return !!axesPositions.find(({ position }) =>
    expectedPositionsIncludes(position),
  );
};

const axisPositionIncludes =
  (expectedPosition: 'left' | 'right') =>
  (actualPosition: 'left' | 'right' | 'both') => {
    return [expectedPosition, 'both'].includes(actualPosition);
  };

type IndicatorData = (args: {
  axisIdToScales: AxisIdToScales;
  threshold: XYChartThresholdsPropsInternal[number];
  width: number;
}) => ThresholdIndicatorData[];

export const mapIndicatorDataFrom: IndicatorData = ({
  axisIdToScales,
  threshold,
  width,
}) => {
  const yScale = getCurrentScale(
    axisIdToScales,
    threshold.yAxisId,
  ) as AnyNumberScale;

  if (isThresholdDynamic(threshold)) {
    const data: XYChartThresholdDynamicData[] = threshold.data;

    const xScale = getCurrentScale(
      axisIdToScales,
      threshold.xAxisId,
    ) as AnyNumberScale;

    const initialSegment = {
      x0: 0,
      x1: 0,
      y0: getThresholdPixel(getYValue(data[0], 'min'), yScale),
      y1: getThresholdPixel(getYValue(data[0], 'max'), yScale),
    };

    const finalSegment = {
      x0: width,
      x1: width,
      y0: getThresholdPixel(getYValue(data[data.length - 1], 'min'), yScale),
      y1: getThresholdPixel(getYValue(data[data.length - 1], 'max'), yScale),
    };

    const segments = data.map((coordinate) => ({
      x0: getThresholdPixel(coordinate.x, xScale),
      x1: getThresholdPixel(coordinate.x, xScale),
      y0: getThresholdPixel(getYValue(coordinate, 'min'), yScale),
      y1: getThresholdPixel(getYValue(coordinate, 'max'), yScale),
    }));

    return [initialSegment, ...segments, finalSegment];
  } else {
    const data = threshold.data as
      | XYChartThresholdDataRangeStatic
      | XYChartThresholdDataLineStatic;

    const yMinScaleValue = getThresholdPixel(getYValue(data, 'min'), yScale);
    const yMaxScaleValue = getThresholdPixel(getYValue(data, 'max'), yScale);

    return [
      {
        x0: 0,
        x1: 0,
        y0: yMinScaleValue,
        y1: yMaxScaleValue,
      },
      {
        x0: width,
        x1: width,
        y0: yMinScaleValue,
        y1: yMaxScaleValue,
      },
    ];
  }
};
